import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { profileAction } from "@redux/modules";
import showMessage from "@components/ShowMessage";
import showErrMessage from "@components/ShowErrMessage";

const TYPE_LABELS = {
  PROFILE: "Profil",
  SCREEN: "Ekran",
  OTHER: "Diğer",
};

const STATUS_META = {
  APPROVED: { label: "Onaylandı", className: "text-green", icon: "fas fa-check-circle" },
  WAITING: { label: "Onay Bekliyor", className: "text-orange", icon: "fas fa-clock" },
  DENIED: { label: "Reddedildi", className: "text-red", icon: "fas fa-times-circle" },
};

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "";

const Photos = () => {
  const dispatch = useDispatch();
  const profilePhoto = useSelector((store) => store.profile.profilePhoto);
  const profileDetail = useSelector((store) => store.profile.profileDetail);
  const [loading, setLoading] = useState(false);

  // KVKK opt-in: yalnızca açıkça izin verildiyse yükleme yapılabilir.
  const isPhotoSharingEnabled =
    profileDetail?.employeeDTO?.isPhotoSharingEnabled === true;

  const [pageState, setPageState] = useState({
    isNewPhotoVisible: false,
    newImage: null,
    newImageFile: null, // Add this to store the file object
    newImageType: "PROFILE",
  });

  const [state, setState] = useState({
    isLoading: false,
    isError: false,
    error: null,
  });

  // Fotoğraf listesi ve fotoğraf izni bilgisini yükle
  useEffect(() => {
    dispatch(profileAction.getProfilePhoto());
    if (!profileDetail) {
      dispatch(profileAction.getLoginUserDetail());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImageUpload = (file, type) => {
    if (!isPhotoSharingEnabled) {
      showErrMessage(
        "Fotoğraf yüklemek için Hesap Bilgilerim - İletişim Tercihlerim menüsünden Fotoğraf paylaşımına izninizi vermeniz gerekmektedir."
      );
      return;
    }

    setState({
      ...state,
      isLoading: true,
    });
    setLoading(true);

    dispatch(profileAction.imageUpload(file, type)).then((res) => {
      if (res.isHttpSuccess) {
        const imageType = TYPE_LABELS[type] || "Diğer";
        showMessage(
          `${imageType} fotoğrafınız başarıyla yüklendi. Bekleme sekmesinden kontrol edebilirsiniz. Onaylandıktan sonra işleme alınacaktır!`
        );
        dispatch(profileAction.getProfilePhoto());
        // Reset states
        setPageState({
          ...pageState,
          isNewPhotoVisible: false,
          newImage: null,
          newImageFile: null,
          newImageType: "PROFILE",
        });
      } else {
        showErrMessage("Fotoğraf yüklenirken bir hata oluştu.");
      }
      setLoading(false);
      setState({ ...state, isLoading: false });
    });
  };

  const handleImageRemove = (e, imageId) => {
    e.preventDefault();

    dispatch(profileAction.removeImageById(imageId))
      .then(() => {
        showMessage("Görsel başarılı bir şekilde silindi!");
        dispatch(profileAction.getProfilePhoto());
      })
      .catch((err) => {
        showErrMessage(err.message);
      });
  };

  const handleImageChange = (event) => {
    event.preventDefault();
    if (event.target.files?.[0]) {
      setPageState({
        ...pageState,
        newImage: window.URL.createObjectURL(event.target.files[0]),
        newImageFile: event.target.files[0], // Store the file object
      });
    }
  };

  // "Yeni Fotoğraf Ekle" / "Yeniden Yükle" akışını başlat
  const openNewPhotoForm = (type = "PROFILE") => {
    if (!isPhotoSharingEnabled) {
      showErrMessage(
        "Fotoğraf yüklemek için Hesap Bilgilerim - İletişim Tercihlerim menüsünden Fotoğraf paylaşımına izninizi vermeniz gerekmektedir."
      );
      return;
    }
    setPageState({
      ...pageState,
      isNewPhotoVisible: true,
      newImage: null,
      newImageFile: null,
      newImageType: type,
    });
  };

  const photos = Array.isArray(profilePhoto) ? profilePhoto : [];
  const activePhotos = photos.filter((item) => item.imageStatus !== "DENIED");
  const rejectedPhotos = photos.filter((item) => item.imageStatus === "DENIED");

  const renderPhotoCard = (item, index) => {
    const status = STATUS_META[item.imageStatus] || STATUS_META.WAITING;
    const typeLabel = TYPE_LABELS[item.employeeImageContentType] || "Diğer";
    const isDenied = item.imageStatus === "DENIED";

    return (
      <div className="col" key={item.id ?? index}>
        <div className="border rounded-3 overflow-hidden h-100 d-flex flex-column">
          <div
            className="bg-light d-flex align-items-center justify-content-center"
            style={{ height: "200px" }}
          >
            <img
              src={item.imageUrl}
              alt=""
              className="img-contain w-100 h-100"
            />
          </div>
          <div className="p-3 d-flex flex-column gap-2 flex-grow-1">
            <div className="d-flex justify-content-between align-items-center">
              <span
                className="badge border"
                style={{ backgroundColor: "#f1f3f5", color: "#212529" }}
              >
                {typeLabel}
              </span>
              <span className={`small ${status.className}`}>
                <i className={`${status.icon} me-1`}></i>
                {status.label}
              </span>
            </div>

            {item.dateUpload ? (
              <div className="text-muted small">
                Yüklenme: {formatDate(item.dateUpload)}
              </div>
            ) : null}

            {isDenied ? (
              <React.Fragment>
                {item.rejectNote ? (
                  <div className="small text-red">
                    <strong>Red nedeni:</strong> {item.rejectNote}
                  </div>
                ) : null}
                {item.statusDate ? (
                  <div className="text-muted small">
                    Reddedilme: {formatDate(item.statusDate)}
                  </div>
                ) : null}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary mt-1 align-self-start"
                  onClick={() =>
                    openNewPhotoForm(item.employeeImageContentType || "PROFILE")
                  }
                >
                  <i className="fas fa-redo me-1"></i> Yeniden Yükle
                </button>
              </React.Fragment>
            ) : null}

            <button
              type="button"
              className="btn btn-link text-red text-decoration-none p-0 small mt-auto align-self-start"
              onClick={(event) => handleImageRemove(event, item.id)}
            >
              <i className="fas fa-trash-alt me-1"></i> Sil
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="d-flex align-items-center justify-content-between mb-sm-6 mb-4">
        <h2 className="mb-0">Fotoğraflarım</h2>
        {!pageState.isNewPhotoVisible ? (
          <button
            type="button"
            className="btn btn-link text-decoration-none"
            onClick={() => openNewPhotoForm("PROFILE")}
          >
            <i className="fas fa-plus-circle"></i> Yeni Fotoğraf Ekle
          </button>
        ) : null}
      </div>
      <p className="mb-sm-5 mb-4 fw-light">
        Bu bölümde profil ve dükkan fotoğraflarınızı ekleyebilir veya
        güncelleyebilirsiniz. Profil fotoğrafınızın onaylanabilmesi için, lütfen
        yüzünüzün net olarak göründüğü bir fotoğraf seçin. Dükkan fotoğrafınızın
        onaylanabilmesi içinse, dükkanınızın içeriden veya dışarıdan geniş açı
        ile çekilmiş bir fotoğrafını yükleyin. Yüklediğiniz fotoğraflar için
        onay mesajını bir hafta içinde Mesajlarım bölümünde görebilirsiniz.
        Fotoğrafınız ile ilgili mesaj almadığınız takdirde “Bize Ulaşın” formunu
        doldurarak veya 0850 335 1358 Cargill Gurme Club Çağrı Merkezi’ni
        arayarak detaylı bilgi alabilirsiniz.
      </p>

      {!isPhotoSharingEnabled ? (
        <div className="alert alert-warning d-flex align-items-start gap-2" role="alert">
          <i className="fas fa-exclamation-triangle mt-1"></i>
          <div>
            Fotoğraf yüklemek için{" "}
            <Link
              to="/profile/communication-preferences"
              className="alert-link text-decoration-none"
            >
              Hesap Bilgilerim - İletişim Tercihlerim
            </Link>{" "}
            menüsünden Fotoğraf paylaşımına izninizi vermeniz gerekmektedir.
          </div>
        </div>
      ) : null}

      {pageState.isNewPhotoVisible ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleImageUpload(pageState.newImageFile, pageState.newImageType);
          }}
        >
          <div className="d-flex align-items-center justify-content-between mb-sm-5 mb-4">
            <h5 className="text-blue mb-0">Yeni Fotoğraf Ekle</h5>
            <button
              type="button"
              className="btn btn-link text-red text-decoration-none"
              onClick={() => {
                setPageState({
                  ...pageState,
                  isNewPhotoVisible: false,
                  newImage: null,
                  newImageFile: null,
                  newImageType: "PROFILE",
                });
              }}
            >
              <small>
                <i className="fas fa-times me-2"></i>Vazgeç
              </small>
            </button>
          </div>
          <div className="row gy-4 gx-5 align-items-center pb-sm-5 pb-4 mb-sm-5 mb-4">
            <div className="col">
              <div
                className="shadow-lg rounded-3 position-relative overflow-hidden w-100 d-flex align-items-center justify-content-center"
                style={{ height: "300px" }}
              >
                <input
                  type="file"
                  className="position-absolute w-100 h-100 top-0 start-0 opacity-0"
                  style={{ cursor: "pointer" }}
                  accept="image/*"
                  name="file"
                  onInput={(event) => handleImageChange(event)}
                />
                {pageState.newImage ? (
                  <React.Fragment>
                    <img
                      src={pageState.newImage}
                      alt=""
                      className="w-100 h-100 img-contain position-absolute top-0 start-0"
                    />
                    <button
                      type="button"
                      className="btn btn-link text-white bg-red rounded-circle m-3 text-decoration-none position-absolute top-0 end-0"
                      onClick={() => {
                        setPageState({
                          ...pageState,
                          newImage: null,
                          newImageFile: null,
                        });
                      }}
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </React.Fragment>
                ) : null}
                <div className="text-center">
                  <h3 className="text-green">
                    <i className="fas fa-plus-circle"></i>
                  </h3>
                  <h5 className="text-green">Fotoğraf Yükle</h5>
                </div>
              </div>
            </div>
            <div className="col">
              <h6 className="mb-4">Fotoğraf Türü</h6>

              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="radio"
                  name="type"
                  value="PROFILE"
                  id="PROFILE"
                  checked={pageState.newImageType === "PROFILE"}
                  onChange={() =>
                    setPageState({ ...pageState, newImageType: "PROFILE" })
                  }
                />
                <label className="form-check-label" htmlFor="PROFILE">
                  Profil Fotoğrafı
                </label>
              </div>
              <div className="form-check mb-4">
                <input
                  className="form-check-input"
                  type="radio"
                  name="type"
                  value="SCREEN"
                  id="SCREEN"
                  checked={pageState.newImageType === "SCREEN"}
                  onChange={() =>
                    setPageState({ ...pageState, newImageType: "SCREEN" })
                  }
                />
                <label className="form-check-label" htmlFor="SCREEN">
                  Ekran Fotoğrafı
                </label>
              </div>

              <button
                type="submit"
                className="btn btn-blue py-sm-3 py-2 px-sm-4 px-3"
                disabled={
                  !pageState.newImage ||
                  state.isLoading ||
                  !isPhotoSharingEnabled
                }
              >
                {state.isLoading ? (
                  <span className="spinner-border spinner-border-sm me-3"></span>
                ) : null}
                Gönder
              </button>
            </div>
          </div>
        </form>
      ) : null}

      {photos.length > 0 ? (
        <React.Fragment>
          {activePhotos.length > 0 ? (
            <div className="mb-sm-6 mb-5">
              <h5 className="mb-3">Fotoğraflarım</h5>
              <div className="row row-cols-md-3 row-cols-2 g-4">
                {activePhotos.map((item, i) => renderPhotoCard(item, i))}
              </div>
            </div>
          ) : null}

          {rejectedPhotos.length > 0 ? (
            <div>
              <h5 className="mb-3 text-red">
                <i className="fas fa-times-circle me-2"></i>Reddedilen
                Fotoğraflar
              </h5>
              <div className="row row-cols-md-3 row-cols-2 g-4">
                {rejectedPhotos.map((item, i) => renderPhotoCard(item, i))}
              </div>
            </div>
          ) : null}
        </React.Fragment>
      ) : (
        <div className="text-center mt-8">
          <h1 className="mb-3 opacity-75">
            <i className="fas fa-image"></i>
          </h1>
          <h3 className="mb-4 opacity-75">Hiç fotoğrafınız yok</h3>
        </div>
      )}
    </React.Fragment>
  );
};

export default Photos;
